import React from "react";
import { useSelector } from "react-redux";
import { array, bool, string, object } from "prop-types";
import get from "lodash/get";
import { Link } from "@quintype/components";
import { HeroImage } from "../../atoms/HeroImage";
import { PublishAndReadTime } from "../../atoms/PublishAndReadTime";
import { getStoryHeadline, getStoryLabel, isExternalStory } from "../../utils/story";
import { MainStory } from "./MainStory";
import { OtherStory } from "./OtherStory";
import { DfpComponent } from "../../ads/dfp-component";
import { Label } from "../../atoms/Label";
import Headline from "../../atoms/Headline";

import "./three-col-nineteen-stories.m.css";

export const PrimaryColumn = ({
  stories,
  isMobile,
  showHeroImage,
  showReadTime,
  showTimestamp,
  showStorySeparator,
  sectionSlug,
}) => {
  const [firstStory, ...remainingStories] = stories;
  return (
    <div styleName="first-column">
      <MainStory
        story={firstStory}
        showBorder={showStorySeparator}
        showHeroImage={showHeroImage}
        showReadTime={showReadTime}
        showTimestamp={showTimestamp}
        sectionSlug={sectionSlug}
      />
      {remainingStories.map((story, index) => (
        <div key={story.id}>
          <OtherStory
            story={story}
            showReadTime={showReadTime}
            showTimestamp={showTimestamp}
            showThumbnail={true}
            showBorder={showStorySeparator && (isMobile || index !== remainingStories.length - 1)}
            sectionSlug={sectionSlug}
          />
        </div>
      ))}
    </div>
  );
};

PrimaryColumn.propTypes = {
  stories: array,
  showHeroImage: bool,
  isMobile: bool,
  showReadTime: bool,
  showTimestamp: bool,
  showStorySeparator: bool,
  sectionSlug: string,
};

export const SecondaryColumn = ({
  stories,
  showAd,
  rowAdUnit,
  showReadTime,
  showTimestamp,
  showStorySeparator,
  collection,
  sectionSlug,
}) => {
  const rowAdConfig = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "row_ad_story_300x250"], {})
  );
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], null));

  return (
    <div styleName="second-column">
      {showAd && (
        <div styleName="adSlot">
          <DfpComponent
            adStyleName="three-col-nineteen-ad-slot-size-300x250"
            id={`${pageType}-${collection.slug}-row-ad`}
            path={rowAdUnit}
            size={rowAdConfig.sizes}
            viewPortSizeMapping={rowAdConfig.view_port_size_mapping}
          />
        </div>
      )}
      {stories.map((story, index) => (
        <div key={story.id}>
          <OtherStory
            story={story}
            showReadTime={showReadTime}
            showTimestamp={showTimestamp}
            showThumbnail={false}
            showBorder={showStorySeparator && index !== stories.length - 1}
            sectionSlug={sectionSlug}
          />
        </div>
      ))}
    </div>
  );
};

SecondaryColumn.propTypes = {
  stories: array,
  showAd: bool,
  adSlotLabel: string,
  showReadTime: bool,
  showTimestamp: bool,
  showStorySeparator: bool,
  sectionSlug: string,
  collection: object,
  rowAdUnit: string,
};

export const TertiaryColumn = ({
  stories,
  photosLabel,
  showReadTime,
  showTimestamp,
  showStorySeparator,
  showAd,
  rowAdUnit,
  collection,
  deviceType,
  sectionSlug,
}) => {
  const rowAdConfig = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "row_ad_story_300x250"], {})
  );
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], null));
  return (
    <div styleName="third-column">
      {showAd && (
        <div styleName="adSlot">
          <DfpComponent
            adStyleName="three-col-nineteen-ad-slot-size-300x250"
            id={`${pageType}-${collection.slug}-row-ad`}
            path={rowAdUnit}
            size={rowAdConfig.sizes}
            viewPortSizeMapping={rowAdConfig.view_port_size_mapping}
          />
        </div>
      )}
      <div styleName="third-column-stories">
        <div styleName="left-stories">
          {stories.slice(0, 3).map((story, index) => (
            <div key={story.id}>
              <OtherStory
                story={story}
                showReadTime={showReadTime}
                showTimestamp={showTimestamp}
                showThumbnail={false}
                showBorder={showStorySeparator && (deviceType !== "tablet" || index !== 2)}
                sectionSlug={sectionSlug}
              />
            </div>
          ))}
        </div>
        {stories.length >= 4 && (
          <div>
            <div>
              <OtherStory
                story={stories[3]}
                showReadTime={showReadTime}
                showTimestamp={showTimestamp}
                showThumbnail={false}
                showBorder={showStorySeparator && false}
                sectionSlug={sectionSlug}
              />
            </div>
            <div styleName="text-with-line">{photosLabel}</div>
            <div styleName="photos-container">
              {stories.slice(4, 6).map((story) => {
                const label = getStoryLabel(story);
                const imageUrl =
                  get(story, ["hero-image-s3-key"], "") ||
                  get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"], "");
                const externalStoryUrl = isExternalStory(story);
                const href = externalStoryUrl ? externalStoryUrl : `/${story?.slug}`;
                const target = externalStoryUrl ? "_blank" : "_self";
                return (
                  <Link href={href} target={target} key={story.id}>
                    <div styleName="photo-story" style={{ border: showStorySeparator || "none" }}>
                      <HeroImage
                        story={story}
                        headline={story.headline}
                        aspectRatio={[[4, 3]]}
                        iconSizes={{ height: 24, width: 24 }}
                        imageUrl={imageUrl}
                      />
                      {label && <Label story={story} />}

                      {getStoryHeadline(story) && (
                        <Headline styleName="headline" headline={getStoryHeadline(story)} sectionSlug={sectionSlug} />
                      )}
                      <PublishAndReadTime showReadTime={showReadTime} showTimestamp={showTimestamp} story={story} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

TertiaryColumn.propTypes = {
  stories: array,
  photosLabel: string,
  showAd: bool,
  adSlotLabel: string,
  deviceType: string,
  showReadTime: bool,
  showTimestamp: bool,
  showStorySeparator: bool,
  sectionSlug: string,
  collection: object,
  rowAdUnit: string,
};
